// See: https://github.com/LekoArts/gatsby-themes/blob/main/themes/gatsby-theme-minimal-blog/src/components/seo.tsx

import * as React from "react";
import { Helmet } from "react-helmet";
import { withPrefix } from "gatsby";
import useSiteMetadata from "../hooks/use-site-metadata";

function trimImageText(title: string) {
  if (title.length > 60) {
    return title.substring(0, 60) + "…";
  } else {
    return title;
  }
}

type Vercel = {
  title: string;
  subtitle?: string;
  tags?: string;
};

function getVercelUrl(vercel: Vercel) {
  const vercel_og_image_url = "https://og-image-goutam.vercel.app/api/ogimage";
  let vercel_url = vercel_og_image_url + "?title=" + encodeURIComponent(trimImageText(vercel.title))

  if (vercel.subtitle) {
    vercel_url = vercel_url + "&subtitle=" + encodeURIComponent(vercel.subtitle)
  }

  if (vercel.tags) {
    vercel_url = vercel_url + "&tags=" + encodeURIComponent(vercel.tags)
  }

  return vercel_url
}

type SEOProps = {
  title?: string;
  description?: string;
  pathname?: string;
  image?: string;
  vercel?: Vercel;
  children?: React.ReactNode;
  canonicalUrl?: string;
};

const Seo = ({
  title = ``,
  description = ``,
  pathname = ``,
  image = ``,
  vercel = {
    title: ``,
    subtitle: ``,
    tags: ``,
  },
  children = null,
  canonicalUrl = ``,
}: SEOProps) => {
  const site = useSiteMetadata();
  const {
    siteTitle,
    siteTitleAlt: defaultTitle,
    siteUrl,
    siteDescription: defaultDescription,
    siteLanguage,
    siteImage: defaultImage,
    author,
  } = site;

  const seo = {
    title: title || defaultTitle,
    description: description || defaultDescription,
    url: `${siteUrl}${pathname || ``}`,
    image: vercel ? getVercelUrl(vercel) : `${siteUrl}${image || defaultImage}`,
  };
  return (
    <Helmet
      title={title}
      defaultTitle={defaultTitle}
      titleTemplate={`%s | ${siteTitle}`}
    >
      <html lang={siteLanguage} />
      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:type" content="website" />
      <meta property="og:image:alt" content={seo.description} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:url" content={seo.url} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      <meta name="twitter:image:alt" content={seo.description} />
      <meta name="twitter:creator" content={author.name} />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href={withPrefix(`/favicon-32x32.png`)}
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href={withPrefix(`/favicon-16x16.png`)}
      />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href={withPrefix(`/apple-touch-icon.png`)}
      />
      {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
      {children}
    </Helmet>
  );
};

export default Seo;
